import PortalLayout from 'components/LayoutContainers/PortalLayout';
import PortalNavbar from 'components/Navbars/PortalNavbar';
import Footer from 'components/Footer';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertTitle, Box, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MDBox from 'components/MDBox';
import { ErrorBoundary } from 'react-error-boundary';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useMaterialUIController } from 'context';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import IdleContainer from 'components/Security/IdleContainer';
import ScrollTop from 'components/Scroll/ScrollTop';
import Icon from '@mui/material/Icon';
import LinearProgress from '@mui/material/LinearProgress';
import { useSelector } from 'react-redux';
import FileViewerModal from '../fileViewer/FileViewerModal';

ErrorFallback.defaultProps = {
  resetErrorBoundary: () => {}
};

ErrorFallback.propTypes = {
  error: PropTypes.object.isRequired,
  resetErrorBoundary: PropTypes.func
};

export default function ErrorFallback({ error, resetErrorBoundary }) {
  const navigate = useNavigate();
  return (
    <Alert
      severity="error"
      sx={{
        '& .MuiAlert-message': {
          width: '100%'
        }
      }}
    >
      <AlertTitle>
        <Grid container spacing={2} justifyContent="space-between">
          <Grid item>
            <MDTypography variant="h4" color="error">
              Error: {error.message}
            </MDTypography>
          </Grid>
          <Grid item>
            <MDButton
              variant="outlined"
              iconOnly
              color="error"
              onClick={() => {
                resetErrorBoundary();
                navigate('/redirectHome');
              }}
            >
              <ArrowBackIcon />
            </MDButton>
          </Grid>
        </Grid>
      </AlertTitle>
      {error.stack && (
        <MDBox sx={{ margin: 'auto', textAlign: 'left' }}>
          <MDTypography variant="body2" color="error">
            <pre>{error.stack}</pre>
          </MDTypography>
        </MDBox>
      )}
    </Alert>
  );
}

export function PortalTemplate() {
  const appState = useSelector((state) => state.app);

  const { setPageTitle } = useMaterialUIController();

  const theme = useTheme();
  const { palette } = theme;
  const { background } = palette;

  const location = useLocation();
  useEffect(() => {
    setPageTitle(null);
  }, [location.key, setPageTitle]);

  const { view } = useParams();
  const { getValue: conf } = useConf(useGetConfigurationQuery);

  return (
    <IdleContainer
      timeout={conf('security.session.timeout')}
      promptTimeout={conf('security.session.promptTimeout')}
    >
      <PortalLayout>
        <PortalNavbar view={view} />
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Outlet />
        </ErrorBoundary>
        <FileViewerModal />
        <Footer />
      </PortalLayout>

      <ScrollTop>
        <Icon fontSize="large" color="inherit">
          keyboard_double_arrow_up
        </Icon>
      </ScrollTop>

      {appState.appLoading && (
        <Box
          position="fixed"
          top={0}
          left={0}
          pb={1}
          sx={{
            width: '100%',
            backgroundColor: background.default
          }}
        >
          <LinearProgress sx={{ borderRadius: 'unset' }} />
        </Box>
      )}
    </IdleContainer>
  );
}
